import React from 'react'
import {
  alignStepShortDescriptionOrdinalWithHeading,
  extractAuditEmbeddedTreePathFromShortDescription,
} from "../../../utils/previewUtils";

import RichText from "./Nodes/RichText";
import Attachment from "./Nodes/Attachment";
import Video from "./Nodes/Video";
import Canvas from "./Nodes/Canvas";

const RenderNodes = ({
  nodeIndices,
  children,
  sectionIdx,
  stepIdx,
  substepIdx = undefined,
  treepath,
  showCheckbox,
  selectedNodes,
  onCheckboxChange,
  disabled = false,
  sectionIsSelected = false,
  stepIsDirectlySelected = false,
  substepIsSelected = false,
  readOnlyCheckboxTitleMatch = null,
  hideRichTextFieldLabels = false,
  /** Matches Step heading ordinal; aligns lead "Step N" in child short_description with parent step */
  stepDisplayOrdinal = null,
}) => {
    const getNodeHeading = (content, globalCount) => {
      // Prefer node identifier (pageTitle, name) so heading is "Rich Text 1" not the description text
      const candidate =
        content?.pageTitle ||
        content?.name ||
        content?.title;
      if (typeof candidate === 'string' && candidate.trim().length > 0) {
        return candidate.trim();
      }
      const typeLabel = content?.type
        ? content.type
            .split('-')
            .map((p) => (p ? p[0].toUpperCase() + p.slice(1) : p))
            .join(' ')
        : 'Node';
      // Use "Rich Text 1", "Attachment 2" etc. as heading; only use short_description if no type/count
      if (typeLabel && globalCount != null) {
        return `${typeLabel} ${globalCount}`;
      }
      if (typeof content?.short_description === 'string' && content.short_description.trim().length > 0) {
        return content.short_description.trim();
      }
      return typeLabel || 'Node';
    };
    
    const getNodeGlobalIndex = (nodeIndices, childIdx, childIds = []) => {
        let found = undefined 
        const normalizedIds = (childIds || []).map((id) => String(id));
        if (normalizedIds.length > 0) {
          found = nodeIndices.find(
            (i) =>
              i?.childStableId &&
              normalizedIds.includes(String(i.childStableId)),
          );
        }
        if(substepIdx!==undefined){
          if (!found) {
            found = nodeIndices.find(
              i => i.sectionIdx === sectionIdx && i.stepIdx === stepIdx && i.childIdx === childIdx && i.substepIdx === substepIdx
            );
          }
        }else{
          if (!found) {
            found = nodeIndices.find(
              i => i.sectionIdx === sectionIdx && i.stepIdx === stepIdx && i.childIdx === childIdx
            );
          }
        }
        return found ? found.globalIndex : null;
    };
    
    return <>
        {children?.map((child,index) => {
        if (!child) return null;
        const rawContent = child?.version_details?.version_content || child?.detail || child || {};
        const childContent = {
          ...rawContent,
          type: rawContent.type ?? child?.type,
        };
        const contentForDisplay =
          stepDisplayOrdinal != null &&
          childContent.short_description != null &&
          childContent.short_description !== ""
            ? {
                ...childContent,
                short_description: alignStepShortDescriptionOrdinalWithHeading(
                  childContent.short_description,
                  stepDisplayOrdinal,
                ),
              }
            : childContent;
        /** Procedure tree_node_id first — matches audit preview_tree_node_id; detail.*_tree_node_id is entity ref */
        const childCheckIds = [
          child?.tree_node_id,
          childContent.element_id,
          childContent.canvas_tree_node_id,
          childContent.canva_tree_node_id,
          childContent.rich_text_node_id,
          childContent.rich_text_tree_node_id,
          childContent.video_tree_node_id,
          childContent.attachment_tree_node_id,
          childContent.preview_tree_node_id,
        ].filter(Boolean);
        const childToggleId = childCheckIds[0];

        const handleChildCheckboxChange = () => {
          if (onCheckboxChange && childToggleId) {
            onCheckboxChange(childToggleId);
          }
        };
        
        let node = <></>;
        const isChildChecked =
          childCheckIds.some((id) => selectedNodes?.[id]) ||
          sectionIsSelected ||
          stepIsDirectlySelected ||
          substepIsSelected;

        const embeddedTreePath = extractAuditEmbeddedTreePathFromShortDescription(
          contentForDisplay?.short_description,
        );

        const nodeProps = {
          content: contentForDisplay,
          globalCount: null,
          heading: "",
          treepath: embeddedTreePath || treepath,
          showCheckbox: showCheckbox,
          isChecked: isChildChecked,
          onCheckboxChange: handleChildCheckboxChange,
          disabled: disabled,
          readOnlyCheckboxTitleMatch,
          hideRichTextFieldLabels,
        };
        
        switch (childContent.type) {
          case "rich-text":
            nodeProps.globalCount = getNodeGlobalIndex(nodeIndices.richTextIndices, index, childCheckIds);
            nodeProps.heading = getNodeHeading(contentForDisplay, nodeProps.globalCount);
            node = <RichText {...nodeProps} />;
            break;
          case "attachment":
            nodeProps.globalCount = getNodeGlobalIndex(nodeIndices.attachmentIndices, index, childCheckIds);
            nodeProps.heading = getNodeHeading(contentForDisplay, nodeProps.globalCount);
            node = <Attachment {...nodeProps} />;
            break;
          case "video":
            nodeProps.globalCount = getNodeGlobalIndex(nodeIndices.videoIndices, index, childCheckIds);
            nodeProps.heading = getNodeHeading(contentForDisplay, nodeProps.globalCount);
            node = <Video {...nodeProps} />;
            break;
          case "canvas":
            nodeProps.globalCount = getNodeGlobalIndex(nodeIndices.canvasIndices, index, childCheckIds);
            nodeProps.heading = getNodeHeading(contentForDisplay, nodeProps.globalCount);
            node = <Canvas {...nodeProps} />;
            break;
          case "canva":
            nodeProps.globalCount = getNodeGlobalIndex(nodeIndices.canvasIndices, index, childCheckIds);
            nodeProps.heading = getNodeHeading(contentForDisplay, nodeProps.globalCount);
            node = <Canvas {...nodeProps} />;
            break;
          default:
            break;
        }
        return node;
      })}
    </>
}

export default RenderNodes